// SPDX-License-Identifier: AGPL-3.0-or-later

import * as Modal from '@app/features/app/components/dialogs/Modal';
import {Endpoints} from '@app/features/app/constants/Endpoints';
import {useFormSubmit} from '@app/features/app/hooks/useFormSubmit';
import type {DeveloperApplication} from '@app/features/devtools/models/DeveloperApplication';
import {conduit} from '@app/features/platform/transport/RestTransport';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {Form} from '@app/features/ui/components/form/Form';
import {Input} from '@app/features/ui/components/form/Input';
import styles from '@app/features/user/components/modals/tabs/applications_tab/ApplicationsTab.module.css';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useRef} from 'react';
import {useForm} from 'react-hook-form';

interface ApplicationCreateModalProps {
	onCreated: (application: DeveloperApplication) => void;
}
interface CreateFormValues {
	name: string;
}
export const ApplicationCreateModal: React.FC<ApplicationCreateModalProps> = observer(({onCreated}) => {
	const {t} = useLingui();
	const form = useForm<CreateFormValues>({
		defaultValues: {
			name: '',
		},
	});
	const nameField = form.register('name', {required: true, maxLength: 100});
	const nameInputRef = useRef<HTMLInputElement | null>(null);
	const handleCancel = useCallback(() => {
		form.reset();
		form.clearErrors();
		ModalCommands.pop();
	}, [form]);
	const onSubmit = useCallback(
		async (data: CreateFormValues) => {
			const response = await conduit.post<DeveloperApplication>(Endpoints.OAUTH_APPLICATIONS, {
				body: {
					name: data.name.trim(),
					redirect_uris: [],
				},
			});
			onCreated(response.body);
			form.reset();
			ModalCommands.pop();
		},
		[form, onCreated],
	);
	const {handleSubmit, isSubmitting} = useFormSubmit({
		form,
		onSubmit,
		defaultErrorField: 'name',
	});
	return (
		<Modal.Root size="small" centered initialFocusRef={nameInputRef}>
			<Form form={form} onSubmit={handleSubmit}>
				<Modal.Header title={t`Create Application`} />
				<Modal.Content className={styles.createForm}>
					<Input
						type="text"
						label={t`Application Name`}
						{...nameField}
						ref={(el) => {
							nameField.ref(el);
							nameInputRef.current = el;
						}}
						placeholder={t`My Application`}
						maxLength={100}
						required
						disabled={isSubmitting}
						autoFocus
						error={form.formState.errors.name?.message}
					/>
				</Modal.Content>
				<Modal.Footer>
					<Button type="button" variant="secondary" onClick={handleCancel} disabled={isSubmitting}>
						{t`Cancel`}
					</Button>
					<Button type="submit" variant="primary" submitting={isSubmitting}>
						{t`Create`}
					</Button>
				</Modal.Footer>
			</Form>
		</Modal.Root>
	);
});
